import { defineStore } from 'pinia'
import { useSupabaseClient, useRuntimeConfig } from '#imports'
import type { User } from '@supabase/supabase-js'

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  isInitialized: boolean;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    loading: false,
    error: null,
    isInitialized: false
  }),

  getters: {
    isAuthenticated: (state) => !!state.user
  },

  actions: {
    // Initialize user on load
    async initialize() {
      // Only run full initialization once, after that just check session
      if (this.isInitialized) {
        await this.checkSession()
        return
      }
      
      this.loading = true
      
      try {
        const supabase = useSupabaseClient()
        
        // First get the session which should be in the cookie
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()
        
        if (sessionError) {
          console.error('[AUTH] Session error:', sessionError.message)
          throw sessionError
        }
        
        // If we have a session, verify the user
        if (session) {
          const { data: { user: authUser }, error: userError } = await supabase.auth.getUser()
          
          if (userError) {
            console.error('[AUTH] User verification error:', userError.message)
            throw userError
          }
          
          this.user = authUser
        } else {
          this.user = null
        }
        
        // Set up listener for auth state changes
        const { data: { subscription } } = await supabase.auth.onAuthStateChange(
          async (event, session) => {
            console.log('[AUTH] Auth state changed:', event, 'Session:', !!session)
            
            if (session) {
              // Verify the user when we get a new session
              const { data: { user: verifiedUser }, error: verifyError } = await supabase.auth.getUser()
              
              if (verifyError) {
                console.error('[AUTH] User verification error:', verifyError.message)
                this.user = null
                return
              }
              
              this.user = verifiedUser
            } else {
              this.user = null
            }
          }
        )

        this.isInitialized = true
        return () => subscription?.unsubscribe()
      } catch (err: any) {
        console.error('[AUTH] Auth initialization error:', err.message)
        this.error = err.message
        this.user = null
      } finally {
        this.loading = false
      }
    },

    // Check current session
    async checkSession(immediate = false) {
      try {
        const supabase = useSupabaseClient()
        
        // First get the session from cookie
        const { data: { session }, error: sessionError } = await supabase.auth.getSession()
        
        if (sessionError) {
          console.error('[AUTH] Session check error:', sessionError.message)
          this.user = null
          return { session: null, isAuthenticated: false }
        }
        
        // If we have a session, verify the user
        if (session) {
          const { data: { user: authUser }, error: userError } = await supabase.auth.getUser()
          
          if (userError || !authUser) {
            console.error('[AUTH] User verification error:', userError?.message || 'No user data')
            this.user = null
            return { session: null, isAuthenticated: false }
          }
          
          // Update user state if it changed or forced
          if (!this.user || this.user.id !== authUser.id || immediate) {
            this.user = authUser
          }
          
          return { session, isAuthenticated: true }
        }
        
        this.user = null
        return { session: null, isAuthenticated: false }
      } catch (err: any) {
        console.error('[AUTH] Session check error:', err.message)
        this.user = null
        return { session: null, isAuthenticated: false }
      }
    },

    // Force update auth state
    async forceUpdateAuthState() {
      return await this.checkSession(true)
    },

    // Login with email and password
    async login(email: string, password: string) {
      this.loading = true
      this.error = null
      
      try {
        const supabase = useSupabaseClient()
        
        const { data, error: authError } = await supabase.auth.signInWithPassword({
          email,
          password
        })
        
        if (authError) throw authError
        
        // After login, verify user with getUser() to ensure we have authenticated data
        const { data: { user: verifiedUser }, error: verifyError } = await supabase.auth.getUser()
        if (verifyError) throw verifyError
        
        // Use the verified user data
        this.user = verifiedUser
        
        return { success: true, data }
      } catch (err: any) {
        console.error('[AUTH] Login error:', err.message)
        this.error = err.message
        return { success: false, error: err.message }
      } finally {
        this.loading = false
      }
    },

    // Sign up with email, password, and full name
    async signup(email: string, password: string, fullName: string) {
      this.loading = true
      this.error = null
      
      try {
        const supabase = useSupabaseClient()
        
        // Sign up the user
        const { data, error: authError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName
            }
          }
        })
        
        if (authError) throw authError
        
        // Verify user with getUser() to ensure we have authenticated data
        // Note: For email confirmation flow, user might not be authenticated yet
        const { data: { user: verifiedUser } } = await supabase.auth.getUser()
        this.user = verifiedUser
        
        return { success: true, data }
      } catch (err: any) {
        this.error = err.message
        return { success: false, error: err.message }
      } finally {
        this.loading = false
      }
    },

    // Login with Google
    async loginWithGoogle() {
      this.loading = true
      this.error = null
      
      try {
        const supabase = useSupabaseClient()
        const config = useRuntimeConfig()
        
        // Determine the correct redirect URL based on environment
        let redirectUrl = `${window.location.origin}/auth/callback`
        
        // If in production and on Vercel, use the Vercel URL instead of relying on window.location
        if (config.public.nodeEnv === 'production' && config.public.vercelUrl) {
          const vercelUrl = config.public.vercelUrl
          redirectUrl = `https://${vercelUrl}/auth/callback`
        }
        
        const { data, error: authError } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: redirectUrl
          }
        })
        
        if (authError) throw authError
        
        // Note: For OAuth we don't verify here because this redirects the user
        // The verification will happen in the onAuthStateChange handler upon return
        
        return { success: true, data }
      } catch (err: any) {
        this.error = err.message
        return { success: false, error: err.message }
      } finally {
        this.loading = false
      }
    },

    // Logout current user
    async logout() {
      this.loading = true
      this.error = null
      
      try {
        const supabase = useSupabaseClient()
        
        const { error: authError } = await supabase.auth.signOut()
        
        if (authError) throw authError
        
        // Clear user data on logout
        this.user = null
        
        // Verify logout by checking user again
        const { data: { user: verifiedUser } } = await supabase.auth.getUser()
        if (verifiedUser) {
          console.warn('[AUTH] User still authenticated after logout attempt')
        }
        
        return { success: true }
      } catch (err: any) {
        this.error = err.message
        return { success: false, error: err.message }
      } finally {
        this.loading = false
      }
    },

    // Request password reset email
    async resetPassword(email: string) {
      this.loading = true
      this.error = null
      
      try {
        const supabase = useSupabaseClient()
        const config = useRuntimeConfig()
        
        // Determine the correct redirect URL based on environment
        let redirectUrl = `${window.location.origin}/auth/reset-password`
        
        // If in production and on Vercel, use the Vercel URL instead of relying on window.location
        if (config.public.nodeEnv === 'production' && config.public.vercelUrl) {
          const vercelUrl = config.public.vercelUrl
          redirectUrl = `https://${vercelUrl}/auth/reset-password`
        }
        
        const { error: authError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: redirectUrl,
        })
        
        if (authError) throw authError
        
        return { success: true }
      } catch (err: any) {
        this.error = err.message
        return { success: false, error: err.message }
      } finally {
        this.loading = false
      }
    },

    // Update user password (for use after reset)
    async updatePassword(password: string) {
      this.loading = true
      this.error = null
      
      try {
        const supabase = useSupabaseClient()
        
        const { error: authError } = await supabase.auth.updateUser({
          password: password
        })
        
        if (authError) throw authError
        
        // Verify user after password update
        const { data: { user: verifiedUser }, error: verifyError } = await supabase.auth.getUser()
        if (verifyError) throw verifyError
        
        // Update user state with verified data
        this.user = verifiedUser
        
        return { success: true }
      } catch (err: any) {
        this.error = err.message
        return { success: false, error: err.message }
      } finally {
        this.loading = false
      }
    }
  },

  persist: true
}) 